'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { SearchNormal1 } from 'iconsax-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { ApiRoutes, BASE_API_URL } from '@/api/routes'
import SearchItem from './SearchItem'

const createQueryString = (name: string, value: string) => {
  const params = new URLSearchParams()
  params.set(name, value)

  return params.toString()
}

const SearchPage = () => {
  const t = useTranslations()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [data, setData] = useState<any | null>(null)
  const [status, setStatus] = useState<string>('')

  const search = searchParams.get('search')

  const categories: any = {
    news: { name: t('Routes.news'), slug: 'news' },
    articles: { name: t('Routes.articles'), slug: 'articles' },
    projects: { name: t('Routes.projects'), slug: 'projects' },
    services: { name: t('Routes.services'), slug: 'services' },
    vacancies: { name: t('Routes.vacancies'), slug: 'vacancies' },
  }

  useEffect(() => {
    if (data && Object.values(data).every((arr: any) => arr.length === 0)) {
  setStatus(t('Search.notFound'))
}
  }, [data, searchQuery])

  useEffect(() => {
    if (search && !searchQuery.trim().length) {
      setSearchQuery(search)
    }
  }, [])

  useEffect(() => {
    setStatus(t('Search.loading'))
    if (searchQuery.trim().length || search) {
      setData(null)

      const postData = setTimeout(() => {
        try {
          fetch(BASE_API_URL + ApiRoutes.search, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            search: searchQuery.trim().length ? searchQuery.trim() : search,
          }),
        })
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Search request failed: ${response.status}`)
            }

            return response.json()
          })
          .then((content) => {
            setData(content)
          })
          .catch((error) => {
            console.error(error)
            setData(null)
            setStatus('Ошибка поиска. Попробуйте еще раз.')
          })
        } catch (e) {
          console.error(e)
        }
      }, 1000)

      return () => clearTimeout(postData)
    } else {
      setData(null)
      setStatus(t('Search.type'))
    }
  }, [searchQuery, search])

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value) {
      router.push(pathname + '?' + createQueryString('search', e.target.value), {
        scroll: false,
      })
    } else {
      router.push(pathname, {
        scroll: false,
      })
    }
  }

  return (
    <div className="container mx-auto flex flex-col gap-4 px-4 py-6 md:flex-row md:gap-6 md:py-12">
      <div className="min-h-screen flex-grow">
        <div className="relative mb-6">
          <SearchNormal1 size={20} className="absolute left-5 top-4 text-secondary" />
          <input
            type="text"
            placeholder={t('Search.search')}
            className=" w-full rounded-lg border border-secondary-light bg-white px-[48px] py-[13px] outline-none duration-200 ease-in focus:border-secondary disabled:cursor-not-allowed disabled:bg-secondary-light"
            value={searchQuery}
            onChange={inputHandler}
          />
        </div>
        <div className="flex flex-col gap-2">
          {(searchQuery || search) && data ? <h5>{t('Search.result')}</h5> : null}

          {data && Object.values(data).some((arr: any) => arr.length > 0) ? (
            <>
              {Object.keys(categories).map((categoryKey) =>
                (data[categoryKey] ?? []).map((val: any, i: number) => (
                  <SearchItem key={val.slug + i} {...val} category={categories[categoryKey]} />
                ))
              )}
            </>
          ) : status ? (
            <p>{status}</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default SearchPage
